import { useMutation, useQueryClient } from '@tanstack/react-query';

// The empty-retrieval WRITE («Recuperación vacía» — the courier opened the «buzón» to retrieve the
// parcel and found it empty), sibling of useConfirmDeliveryCollect, useEnableDelivery,
// useNonCollectDelivery, useRejectDelivery and useRetrieveDelivery. The non-retrieve fn is injected
// (DI, MSW-testable). retry: false — a WRITE that force-transitions state must never be silently
// re-sent. On success, invalidate the detail (so the card refetches the new non_retrieved state,
// data-driven) AND the user-deliveries list by prefix — the hook does not know the userId, so the
// default exact: false matches every ['user-deliveries', *], keeping the list coherent when the agent
// returns to it. The code-agnostic refetch-on-error lives in DeliveryLayout, not here.
export function useNonRetrieveDelivery(
  id: string,
  nonRetrieveDelivery: (id: string) => Promise<void>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => nonRetrieveDelivery(id),
    retry: false,
    // Fire-and-forget: do NOT return the promise. Returning it makes TanStack await the refetch
    // before the mutation settles, which would hide the refetch skeleton the caller drives off the
    // query's own fetching state.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery-detail', id] });
      void queryClient.invalidateQueries({ queryKey: ['user-deliveries'] });
      // …and the same list reached from a building's tab: same rows, other entry point.
      void queryClient.invalidateQueries({ queryKey: ['location-deliveries'] });
      // The three box-level reads below are invalidated by PREFIX because they are keyed by BOX or
      // LOCK id, which this hook does not know — and, as in the retrieve write, none of the three is
      // a precaution here: the «buzón» was opened, only to be found empty.
      // The box log gains that opening, so a re-unfold revalidates.
      void queryClient.invalidateQueries({ queryKey: ['locker-log'] });
      // The box-status notice must refetch too: whether an empty retrieval frees the reservation is
      // the backend's call, so the box's status can come back changed.
      void queryClient.invalidateQueries({ queryKey: ['box-status'] });
      // …and the lock codes, for the same reason: the physical opening behind this registration may
      // have consumed a code sequence and moved the pointer, so the carriles must not keep painting
      // the sequence read before it.
      void queryClient.invalidateQueries({ queryKey: ['lock-codes'] });
    },
  });
}
